import axios from "axios";
import { isNative, kv } from "./platform";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const AUTH_TOKEN_KEY = "tc-auth-token";

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// On mobile (Capacitor), cookies do not flow reliably across origins.
// Attach the token from Preferences as Authorization: Bearer <jwt>.
// On web, this is a no-op unless the server also set one via /auth/login body.
api.interceptors.request.use(async (config) => {
  if (isNative()) {
    const token = await kv.get(AUTH_TOKEN_KEY);
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Response interceptor — flatten FastAPI Pydantic 422 detail (list of objects)
// into a human-readable string BEFORE any `toast.error(err.response.data.detail)`
// call reaches sonner. Without this, rendering the array as a React child throws
// "Objects are not valid as a React child" and crashes the whole page.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const d = err?.response?.data?.detail;
    if (d != null && typeof d !== "string") {
      err.response.data.detail = formatApiError(d);
    }
    return Promise.reject(err);
  },
);

/** Store a fresh access token — called after /auth/login and /auth/google. */
export async function persistAuthToken(token) {
  if (!token) {
    await kv.remove(AUTH_TOKEN_KEY);
    return;
  }
  await kv.set(AUTH_TOKEN_KEY, token);
}

/** Clear on logout. */
export async function clearAuthToken() {
  await kv.remove(AUTH_TOKEN_KEY);
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
